/**
 * PSR-5c — Talkwalker Consumer Intelligence API sentiment fetcher.
 *
 * Mirror of the Brandwatch provider (`brandwatchSentiment.ts`) for
 * Talkwalker. Shares the same:
 *  - Per-tenant API key contract (`tenants/{tenantId}/integrations/talkwalker.apiKey`).
 *  - Daily query budget (`TALKWALKER_DAILY_QUERY_BUDGET = 100`).
 *  - Token-bucket rate limit (5 burst + 1/min refill — independent
 *    buckets per vendor, intentional).
 *  - Confidence cap (0.70 — calibrated classifier).
 *
 * Endpoint (version-pinned per R2):
 *   https://api.talkwalker.com/api/v3/results
 *
 * Talkwalker's API key arrives as a `accessToken` query parameter (not
 * an `Authorization` header — see Talkwalker docs). The fetcher attaches
 * it server-side; the key is NEVER logged or echoed.
 *
 * Resilience: NEVER throws. Every failure path collapses into
 * `{ status: 'unavailable', reason, entries: [] }`.
 */
import type { PublicSentimentEntry } from '../../../types/siteEvidence';
import type { PublicSentimentSourceResult } from '../../publicSentimentOrchestrator';
import { rethrowIfAborted, throwIfAborted } from '../../../lib/abortSignal';
import {
  classifyBtmTech,
  truncateSnippet,
} from '../sentimentPolarity';

/* ── Public constants ──────────────────────────────────────────────── */

/** Version-pinned Talkwalker v3 results endpoint (R2). */
export const TALKWALKER_API_URL = 'https://api.talkwalker.com/api/v3/results';

/** Per-tenant daily query budget — mirrors Brandwatch's cap. */
export const TALKWALKER_DAILY_QUERY_BUDGET = 100;

/** Confidence ceiling — calibrated classifier, same anchor as Brandwatch. */
export const TALKWALKER_CONFIDENCE_CAP = 0.70;

/** Placeholder USD per-query estimate. Used only by the admin
 *  cost-envelope tile. Talkwalker is typically priced ~1.5× Brandwatch. */
export const TALKWALKER_USD_PER_QUERY = 0.15;

/* ── Token bucket (independent of Brandwatch's bucket) ─────────────── */

const RATE_LIMIT_CAPACITY = 5;
const RATE_LIMIT_REFILL_PER_MS = 1 / 60_000;

let bucketTokens = RATE_LIMIT_CAPACITY;
let bucketLastRefillMs = 0;

export function __acquireTalkwalkerRateToken(nowMs: number = Date.now()): boolean {
  if (bucketLastRefillMs === 0) bucketLastRefillMs = nowMs;
  const elapsed = Math.max(0, nowMs - bucketLastRefillMs);
  const refill = elapsed * RATE_LIMIT_REFILL_PER_MS;
  if (refill > 0) {
    bucketTokens = Math.min(RATE_LIMIT_CAPACITY, bucketTokens + refill);
    bucketLastRefillMs = nowMs;
  }
  if (bucketTokens < 1) return false;
  bucketTokens -= 1;
  return true;
}

export function __resetTalkwalkerRateLimit(): void {
  bucketTokens = RATE_LIMIT_CAPACITY;
  bucketLastRefillMs = 0;
}

/* ── Talkwalker entry shape (subset) ───────────────────────────────── */

/**
 * Subset of Talkwalker's v3 result envelope. Talkwalker bundles its
 * sentiment into a numeric `sentiment` field in [-1, +1] (their own
 * normalisation — different convention from Brandwatch's enum). The
 * mapper preserves the sign + magnitude directly, clamped.
 */
export interface TalkwalkerMention {
  id?: string;
  url?: string;
  title?: string;
  content?: string;
  /** Talkwalker's normalised polarity in [-1, +1]. NaN is illegal but
   *  guarded defensively. */
  sentiment?: number | string;
  author?: string;
  published?: string;
}

interface TalkwalkerResponse {
  result_content?: {
    data?: TalkwalkerMention[];
  };
  data?: TalkwalkerMention[];
}

/* ── Mapper ────────────────────────────────────────────────────────── */

/**
 * Map one Talkwalker mention → `PublicSentimentEntry`. Returns null
 * when required fields are missing.
 */
export function mapTalkwalkerMention(m: TalkwalkerMention): PublicSentimentEntry | null {
  const url = m.url?.trim();
  if (!url) return null;
  const classificationText = (m.title?.trim() || m.content?.trim() || '').toString();
  if (!classificationText) return null;

  // Talkwalker emits sentiment as either number or string-encoded number.
  const polarityRaw = typeof m.sentiment === 'number'
    ? m.sentiment
    : typeof m.sentiment === 'string'
      ? parseFloat(m.sentiment)
      : 0;
  const polarity = Number.isFinite(polarityRaw) ? polarityRaw : 0;
  const sentimentScore = Math.max(-1, Math.min(1, polarity));

  const btmTech = classifyBtmTech(classificationText);
  const fetchedAt = new Date().toISOString();

  return {
    sourceId: 'talkwalker',
    fetchedAt,
    btmTech,
    sentimentScore,
    confidence: TALKWALKER_CONFIDENCE_CAP,
    supportingSnippet: truncateSnippet(classificationText),
    url,
  };
}

/* ── Single-query fetcher ──────────────────────────────────────────── */

export interface TalkwalkerSentimentFilters {
  /** Attempt-owned report cancellation/watchdog signal. */
  signal?: AbortSignal;
  /** Tenant API key (Talkwalker `accessToken`). REQUIRED. */
  apiKey: string;
  /** Talkwalker project id. REQUIRED. */
  projectId: string;
  /** Opaque siteId hash for audit trail. Per R5 — NEVER raw site id. */
  queryTag?: string;
  /** Max results per query. */
  maxRecordsPerQuery?: number;
  /** Override the URL (testing only). */
  urlOverride?: string;
}

const DEFAULT_MAX_RECORDS_PER_QUERY = 50;

async function fetchOneQuery(
  query: string,
  filters: TalkwalkerSentimentFilters,
): Promise<PublicSentimentEntry[]> {
  const base = filters.urlOverride ?? TALKWALKER_API_URL;
  const params = new URLSearchParams({
    project: filters.projectId,
    q: query,
    accessToken: filters.apiKey,
    size: String(filters.maxRecordsPerQuery ?? DEFAULT_MAX_RECORDS_PER_QUERY),
  });
  const url = `${base}?${params.toString()}`;

  const controller = new AbortController();
  if (filters.signal?.aborted) controller.abort(filters.signal.reason);
  const onAbort = () => controller.abort(filters.signal?.reason);
  filters.signal?.addEventListener('abort', onAbort, { once: true });
  const timer = setTimeout(
    () => controller.abort(new Error('talkwalker: request timed out')),
    20_000,
  );
  let response: Response;
  try {
    response = await fetch(url, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
    filters.signal?.removeEventListener('abort', onAbort);
  }

  if (!response.ok) {
    // The error message intentionally does NOT include `url` — that
    // would leak the accessToken query param. Status code only.
    throw new Error(`talkwalker: HTTP ${response.status}`);
  }
  const text = await response.text();
  if (!text.trim()) return [];
  let parsed: TalkwalkerResponse;
  try {
    parsed = JSON.parse(text) as TalkwalkerResponse;
  } catch {
    return [];
  }
  const items = parsed.result_content?.data ?? parsed.data ?? [];
  const out: PublicSentimentEntry[] = [];
  for (const m of items) {
    const mapped = mapTalkwalkerMention(m);
    if (mapped) out.push(mapped);
  }
  return out;
}

/* ── Public entry point ────────────────────────────────────────────── */

/**
 * PSR-5c fetcher — structurally satisfies `PublicSentimentSourceFn`
 * once partially-applied with a filter that carries `apiKey + projectId`.
 *
 * NEVER throws.
 */
export async function fetchTalkwalkerSentiment(
  queries: string[],
  filters: TalkwalkerSentimentFilters,
): Promise<PublicSentimentSourceResult> {
  throwIfAborted(filters?.signal);
  if (!filters || !filters.apiKey || !filters.projectId) {
    return {
      status: 'unavailable',
      reason: 'talkwalker: missing apiKey or projectId',
      entries: [],
    };
  }
  if (queries.length === 0) {
    return { status: 'unavailable', reason: 'no queries supplied', entries: [] };
  }

  const allEntries: PublicSentimentEntry[] = [];
  const errors: string[] = [];
  for (const q of queries) {
    throwIfAborted(filters.signal);
    if (!__acquireTalkwalkerRateToken()) {
      errors.push(`${q}: rate-limited`);
      continue;
    }
    try {
      const entries = await fetchOneQuery(q, filters);
      throwIfAborted(filters.signal);
      allEntries.push(...entries);
    } catch (e) {
      rethrowIfAborted(filters.signal, e);
      const msg = (e as Error)?.message ?? String(e);
      errors.push(`${q}: ${msg}`);
    }
  }

  if (allEntries.length === 0 && errors.length === queries.length) {
    return { status: 'unavailable', reason: errors.join('; '), entries: [] };
  }

  // Cross-query URL dedup.
  const seen = new Set<string>();
  const deduped: PublicSentimentEntry[] = [];
  for (const e of allEntries) {
    if (seen.has(e.url)) continue;
    seen.add(e.url);
    deduped.push(e);
  }

  return { status: 'ok', entries: deduped };
}
